import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import toast from 'react-hot-toast';
import { KeyRound, Eye, EyeOff, ShieldCheck } from 'lucide-react';

const C = { or: '#B8960A', noir: '#1A1710', border: '#E8D8A8', orBg: '#FEF9EC' };

const schema = z.object({
  password: z.string()
    .min(8, 'Minimum 8 caractères')
    .regex(/[A-Z]/, 'Au moins une majuscule')
    .regex(/[0-9]/, 'Au moins un chiffre')
    .regex(/[^A-Za-z0-9]/, 'Au moins un caractère spécial'),
  confirm: z.string().min(1, 'Veuillez confirmer votre mot de passe'),
}).refine(d => d.password === d.confirm, { message: 'Les mots de passe ne correspondent pas', path: ['confirm'] });

type FormData = z.infer<typeof schema>;

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { user, setAuth } = useAuthStore();
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!user) return <Navigate to="/login" replace />;
  if (!user.must_change_password) {
    if (user.role === 'CLIENT') return <Navigate to="/app/client/dashboard" replace />;
    if (user.role === 'JURISTE') return <Navigate to="/app/juriste/dashboard" replace />;
    return <Navigate to="/app/admin/dashboard" replace />;
  }

  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<FormData>({ resolver: zodResolver(schema) });

  const pw = watch('password', '');
  const strength = [pw.length >= 8, /[A-Z]/.test(pw), /[0-9]/.test(pw), /[^A-Za-z0-9]/.test(pw)];
  const score = strength.filter(Boolean).length;
  const barColor = score <= 1 ? '#dc2626' : score === 2 ? '#d97706' : score === 3 ? C.or : '#16a34a';
  const barLabel = ['', 'Faible', 'Moyen', 'Fort', 'Très fort'][score];

  const onSubmit = async (data: FormData) => {
    try {
      const res = await api.post('/auth/set-password', { password: data.password });
      const { user: u, accessToken, refreshToken } = res.data.data;
      setAuth(u, accessToken, refreshToken);
      toast.success('Mot de passe défini. Bienvenue !');
      if (u.role === 'CLIENT') navigate('/app/client/dashboard', { replace: true });
      else if (u.role === 'JURISTE') navigate('/app/juriste/dashboard', { replace: true });
      else navigate('/app/admin/dashboard', { replace: true });
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Une erreur est survenue');
    }
  };

  return (
    <div style={{ minHeight: '100dvh', background: 'linear-gradient(135deg, #F9F1E7 0%, #F2E4CC 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 20, boxShadow: '0 8px 40px rgba(26,23,16,0.12)', width: '100%', maxWidth: 440, padding: '40px 36px' }}>
        <div style={{ height: 48, backgroundImage: 'url(/logo_vaeg_assist.png)', backgroundSize: 'contain', backgroundPosition: 'center left', backgroundRepeat: 'no-repeat', marginBottom: 24 }} />

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: C.orBg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <KeyRound size={26} color={C.or} />
          </div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: C.noir, marginBottom: 8 }}>Première connexion</h1>
          <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
            Bonjour <strong style={{ color: C.noir }}>{user.first_name}</strong>, veuillez définir un mot de passe personnel pour sécuriser votre compte.
          </p>
        </div>

        {/* Rules */}
        <div style={{ background: C.orBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 24 }}>
          {[
            { ok: strength[0], label: '8 caractères minimum' },
            { ok: strength[1], label: 'Une lettre majuscule' },
            { ok: strength[2], label: 'Un chiffre' },
            { ok: strength[3], label: 'Un caractère spécial (!@#$...)' },
          ].map(rule => (
            <div key={rule.label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12 }}>
              <ShieldCheck size={14} color={rule.ok ? '#16a34a' : '#9ca3af'} />
              <span style={{ color: rule.ok ? '#15803d' : '#6b7280' }}>{rule.label}</span>
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Nouveau mot de passe</label>
            <div style={{ position: 'relative' }}>
              <input {...register('password')} type={showPw ? 'text' : 'password'} placeholder="••••••••"
                style={{ width: '100%', padding: '10px 42px 10px 14px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              <button type="button" onClick={() => setShowPw(v => !v)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex' }}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{errors.password.message}</p>}
            {pw.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                  {[0, 1, 2, 3].map(i => (
                    <div key={i} style={{ flex: 1, height: 4, borderRadius: 2, background: i < score ? barColor : C.border, transition: 'background 0.2s' }} />
                  ))}
                </div>
                <span style={{ fontSize: 11, color: barColor, fontWeight: 600 }}>{barLabel}</span>
              </div>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Confirmer le mot de passe</label>
            <div style={{ position: 'relative' }}>
              <input {...register('confirm')} type={showConfirm ? 'text' : 'password'} placeholder="••••••••"
                style={{ width: '100%', padding: '10px 42px 10px 14px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              <button type="button" onClick={() => setShowConfirm(v => !v)} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex' }}>
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.confirm && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{errors.confirm.message}</p>}
          </div>

          <button type="submit" disabled={isSubmitting || score < 4}
            style={{ height: 44, background: C.or, color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: (isSubmitting || score < 4) ? 'not-allowed' : 'pointer', opacity: (isSubmitting || score < 4) ? 0.6 : 1, marginTop: 4 }}>
            {isSubmitting ? 'Enregistrement...' : 'Définir mon mot de passe'}
          </button>
        </form>

        <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 20, lineHeight: 1.5 }}>
          Ce mot de passe remplace définitivement le mot de passe temporaire reçu par email.
        </p>
      </div>
    </div>
  );
}
